<template>
  <ResizablePanelLayout
    v-if="props.showDataTable && dataTableRows.length > 0"
    orientation="horizontal"
    storage-key="apex_chart_panel_height"
    :default-size-percent="60"
    :min-primary-size-percent="40"
    :max-primary-size-percent="70"
    :min-secondary-size-percent="30"
  >
    <template #primary>
      <div class="chart_section rounded-lg p-2 m-4 bg_2 border_8">
        <Apexchart
          ref="chartRef"
          width="100%"
          height="100%"
          :type="apexType"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </template>

    <template #secondary>
      <div class="table_section flex flex-col p-4">
        <CustomTabs
          :tabs="dataTabs"
          v-model="activeDataTab"
          content-padding="pt-2"
          extra-header-styles="justify-center"
          tab-header-class="min-w-[100px]"
        >
          <template #tab_content-0>
            <div class="data_table_container">
              <SimpleTable
                :headers="dataTableHeaders"
                :data="summarizedPaginatedRows"
                :pagination="summarizedSimplePagination"
                :show-actions-column="false"
                :show-page-size="false"
                :auto-height="true"
                :fit-to-container-width="true"
                :gap="0"
                :table-props="{ rowHeight: 42 }"
                identifier-key="key"
                class="analytics_table analytics_table_base"
                @update:pagination="handleSummarizedPaginationUpdate"
              >
                <template #bodyCell="{ column, record }">
                  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
                    {{ formatVisualizationDateTimeValue(record[column.dataIndex]) }}
                  </div>
                </template>
              </SimpleTable>
            </div>
          </template>

          <template #tab_content-1>
            <div class="data_table_container">
              <a-spin :spinning="unsummarizedLoading">
                <SimpleTable
                  v-if="unsummarizedData.rows.length > 0"
                  :headers="unsummarizedHeaders"
                  :data="unsummarizedData.rows"
                  :pagination="unsummarizedSimplePagination"
                  :show-actions-column="false"
                  :show-page-size="false"
                  :force-x-virtual="true"
                  :auto-height="true"
                  :fit-to-container-width="true"
                  :gap="0"
                  :table-props="{ rowHeight: 42 }"
                  identifier-key="key"
                  class="analytics_table analytics_table_base"
                  @update:pagination="handleUnsummarizedPaginationUpdate"
                >
                  <template #bodyCell="{ column, record }">
                    <div
                      class="overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                      {{ formatVisualizationDateTimeValue(record[column.dataIndex]) }}
                    </div>
                  </template>
                </SimpleTable>
                <div
                  v-else-if="!unsummarizedLoading"
                  class="flex items-center justify-center py-8 secondary_color"
                >
                  No data available
                </div>
              </a-spin>
            </div>
          </template>
        </CustomTabs>
      </div>
    </template>
  </ResizablePanelLayout>

  <div v-else class="flex flex-col w-full" :style="containerStyle">
    <div class="flex-1 min-h-0 rounded-lg px-2 bg_2 border_8 overflow-hidden">
      <Apexchart
        ref="chartRef"
        width="100%"
        :height="props.height"
        :type="apexType"
        :options="chartOptions"
        :series="chartSeries"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { chartColorOptions } from "@/src/constants/colorSchema.js";
import { daysOfWeeks } from "@/src/constants/meetingAgentConstants.js";
import { formatVisualizationDateTimeValue, normalizeToISO, formatTimestamp, getISOWeekNumber, detectDatesPeriodType, formatDateByPeriod } from "@/src/utils/dateTimeUtils.js";
import {
  formatFullNumber,
  getApexChartType,
  isHorizontalChart,
  decomposeConfigForBackend,
  decomposeFiltersForBackend,
} from "@/src/utils/analyticsChartUtils.js";
import {
  useBarChartOptions,
  useBaseChartOptions,
  useLineAreaChartOptions,
  usePieDonutChartOptions,
  useRadialBarChartOptions,
  useScatterChartOptions,
} from "@/src/composables/charts/chartOptionsFactory.js";
import {
  analyticsBaseColors,
  analyticsEditorTypes,
  apexChartTypes,
  chartConfigFieldKeys,
  dataTableColumns,
  apexDataTabs,
  unsummarizedPageSize,
} from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import CustomTabs from "@/src/components/CustomTabs.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import ResizablePanelLayout from "@/src/components/layout_components/ResizablePanelLayout.vue";

const props = defineProps({
  source: { type: String, default: "apex_chart" },
  chartType: { type: String, required: true },
  chartData: { type: Object, default: () => ({}) },
  height: { type: [String, Number], default: 350 },
  showDataTable: { type: Boolean, default: false },
});

const chartRef = ref(null);
const activeDataTab = ref(0);

const containerStyle = computed(() => {
  if (typeof props.height === 'number') {
    return { height: `${props.height}px` };
  }
  return { height: props.height };
});
const unsummarizedLoading = ref(false);
const unsummarizedData = ref({ rows: [], columns: [], pagination: null });
const unsummarizedFetched = ref(false);

const dataTabs = apexDataTabs;

const effectiveTheme = computed(() => store.getters["theme/getEffectiveTheme"]);

const dashboardId = computed(
  () => store.getters["analytics/selectedDashboard"]?.id,
);

const dashboardType = computed(
  () =>
    store.getters["analytics/selectedDashboard"]?.type ||
    analyticsEditorTypes.MANUAL,
);

const chartConfiguration = computed(
  () => store.getters["analyticsConfiguration/chartConfiguration"],
);

const filters = computed(() => store.getters["analyticsConfiguration/filters"]);

const calculatedFields = computed(
  () => store.getters["analyticsConfiguration/calculatedFields"],
);

const primaryObjectID = computed(
  () => store.getters["analyticsConfiguration/primaryObjectID"],
);

const secondaryObjectIDs = computed(
  () => store.getters["analyticsConfiguration/secondaryObjectIDs"],
);

const isMultiSegmentGauge = computed(() => {
  return (
    props.chartType === "gauge_chart" &&
    props.chartData?.compare_by_field &&
    props.chartData?.series?.length > 1
  );
});

const apexType = computed(() => {
  if (isMultiSegmentGauge.value) {
    return apexChartTypes.DONUT;
  }
  return getApexChartType(props.chartType);
});

const isHorizontal = computed(() => isHorizontalChart(props.chartType));

const rawCategories = computed(() => props.chartData?.categories || []);

const series = computed(() => {
  return props.chartData?.series || [];
});

const labels = computed(() => {
  return props.chartData?.labels || [];
});

const formattedLabels = computed(() => {
  const isDayOfWeek = labels.value.length > 0 && labels.value.every((l) => {
    const n = parseFloat(l);
    return !isNaN(n) && Number.isInteger(n) && n >= 1 && n <= 7;
  });
  if (isDayOfWeek) {
    return labels.value.map((l) => daysOfWeeks[parseInt(l, 10) % 7] ?? l);
  }

  const dates = labels.value
    .map((l) => (typeof l === "string" ? normalizeToISO(l) : null))
    .filter(Boolean)
    .map((iso) => new Date(iso))
    .filter((d) => !isNaN(d.getTime()));
  const periodType = detectDatesPeriodType(dates);

  return labels.value.map((l) => formatDateByPeriod(l, periodType) ?? l);
});

const xAxisLabel = computed(() => props.chartData?.x_axis_label || "");

const yAxisLabel = computed(() => {
  if (props.chartData?.y_axis_label) return props.chartData.y_axis_label;
  if (series.value.length === 1 && series.value[0]?.name) {
    return series.value[0].name;
  }
  return "";
});

const prefix = computed(() => props.chartData?.prefix || "");

const suffix = computed(() => props.chartData?.suffix || "");

const categories = computed(() =>
  rawCategories.value.map((c) => formatVisualizationDateTimeValue(c)),
);

const multiGaugeSeries = computed(() => {
  if (!isMultiSegmentGauge.value) return [];
  const series = props.chartData?.series || [];
  return [...series].reverse();
});

const multiGaugeTotal = computed(() => {
  return (props.chartData?.series || []).reduce(
    (sum, val) => sum + (val || 0),
    0,
  );
});

const multiGaugeOptions = computed(() => {
  if (!isMultiSegmentGauge.value) return {};

  const labelsArray = [...formattedLabels.value].reverse();
  const formattedArray = [
    ...(props.chartData?.formatted_values || []),
  ].reverse();
  const colors = fetchBaseColors();
  const reversedColors = colors
    .slice(0, (props.chartData?.labels || []).length)
    .reverse();
  const total = multiGaugeTotal.value;
  const formattedTotal = total.toLocaleString();

  return {
    chart: {
      type: "donut",
      sparkline: { enabled: false },
    },
    labels: labelsArray,
    colors: reversedColors,
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 90,
        donut: {
          size: "60%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "14px",
              fontFamily: "Proxima Nova, sans-serif",
              color: textColor,
              offsetY: 20,
            },
            value: {
              show: true,
              fontSize: "24px",
              fontFamily: "Proxima Nova, sans-serif",
              fontWeight: 600,
              color: textColor,
              offsetY: -15,
              formatter: () => formattedTotal,
            },
            total: {
              show: true,
              showAlways: true,
              label: "Total",
              fontSize: "12px",
              fontFamily: "Proxima Nova, sans-serif",
              color: textColor,
              formatter: () => formattedTotal,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val, opts) => {
          return formattedArray?.[opts.seriesIndex] || val.toLocaleString();
        },
      },
    },
    stroke: {
      width: 2,
      colors: [isDark.value ? "#1f2937" : "#ffffff"],
    },
  };
});

const chartSeries = computed(() => {
  if (isMultiSegmentGauge.value) {
    return multiGaugeSeries.value || [];
  }
  if (
    apexType.value === apexChartTypes.PIE ||
    apexType.value === apexChartTypes.DONUT
  ) {
    return series.value || [];
  }
  if (apexType.value === apexChartTypes.RADIAL_BAR) {
    return series.value || [0];
  }
  return series.value || [];
});

const fetchBaseColors = () => {
  try {
    return [...chartColorOptions, ...analyticsBaseColors];
  } catch (error) {
    console.error("An error occurred while fetching base colors:", error);
    return [];
  }
};

const getFormattedValue = (seriesIndex, dataIndex) => {
  try {
    const fmtValues = formattedValues.value;
    if (fmtValues?.[seriesIndex]?.[dataIndex]) {
      return fmtValues[seriesIndex][dataIndex];
    }
    const seriesData = series.value[seriesIndex];
    if (seriesData && Array.isArray(seriesData.data)) {
      const value = seriesData.data[dataIndex];
      if (value !== undefined && value !== null) {
        return formatFullNumber(value, prefix.value, suffix.value);
      }
    }
    return "";
  } catch (error) {
    return "";
  }
};

const getPieFormattedValue = (index) => {
  try {
    const fmtValues = formattedValues.value;
    if (fmtValues?.[index]) {
      return fmtValues[index];
    }
    const value = series.value[index];
    if (value !== undefined && value !== null) {
      return formatFullNumber(value, prefix.value, suffix.value);
    }
    return "";
  } catch (error) {
    return "";
  }
};

const decimalPlaces = computed(() => {
  const yAxisFields = chartConfiguration.value?.y_axis || [];
  if (yAxisFields.length > 0 && yAxisFields[0]?.decimal_places !== undefined) {
    return yAxisFields[0].decimal_places;
  }
  return undefined;
});

const { baseOptions, isDark, textColor, gridColor, labelColor } =
  useBaseChartOptions(effectiveTheme, fetchBaseColors());

const barChartOptions = useBarChartOptions({
  baseOptions,
  isDark,
  textColor,
  gridColor,
  labelColor,
  categories,
  xAxisLabel,
  yAxisLabel,
  isHorizontal,
  getFormattedValue,
  decimalPlaces,
});

const lineAreaChartOptions = useLineAreaChartOptions({
  baseOptions,
  isDark,
  textColor,
  gridColor,
  labelColor,
  categories,
  xAxisLabel,
  yAxisLabel,
  getFormattedValue,
  apexType,
  decimalPlaces,
});

const pieDonutChartOptions = usePieDonutChartOptions({
  baseOptions,
  textColor,
  labels: formattedLabels,
  getPieFormattedValue,
  apexType,
  decimalPlaces,
});

const scatterChartOptions = useScatterChartOptions({
  baseOptions,
  textColor,
  gridColor,
  labelColor,
  xAxisLabel,
  yAxisLabel,
});

const formattedValues = computed(() => props.chartData?.formatted_values || []);

const radialBarChartOptions = useRadialBarChartOptions({
  baseOptions,
  isDark,
  textColor,
  labels: formattedLabels,
  series,
  formattedValues,
  getPieFormattedValue,
  decimalPlaces,
});

const chartOptions = computed(() => {
  const chartId = `${props.source}_chart`;

  if (isMultiSegmentGauge.value) {
    return {
      ...baseOptions.value,
      ...multiGaugeOptions.value,
      chart: {
        ...baseOptions.value.chart,
        ...multiGaugeOptions.value.chart,
        id: chartId,
      },
    };
  }

  if (
    apexType.value === apexChartTypes.PIE ||
    apexType.value === apexChartTypes.DONUT
  ) {
    return {
      ...pieDonutChartOptions.value,
      chart: { ...pieDonutChartOptions.value.chart, id: chartId },
    };
  }

  if (apexType.value === apexChartTypes.RADIAL_BAR) {
    return {
      ...radialBarChartOptions.value,
      chart: { ...radialBarChartOptions.value.chart, id: chartId },
    };
  }

  if (apexType.value === apexChartTypes.SCATTER) {
    return {
      ...scatterChartOptions.value,
      chart: { ...scatterChartOptions.value.chart, id: chartId },
    };
  }

  if (
    apexType.value === apexChartTypes.LINE ||
    apexType.value === apexChartTypes.AREA
  ) {
    return {
      ...lineAreaChartOptions.value,
      chart: { ...lineAreaChartOptions.value.chart, id: chartId },
    };
  }

  return {
    ...barChartOptions.value,
    chart: { ...barChartOptions.value.chart, id: chartId },
  };
});

const dataTableHeaders = computed(() => {
  const backendColumns = props.chartData?.summarized_columns;
  const columns =
    backendColumns && backendColumns.length > 0
      ? backendColumns
      : dataTableColumns;
  return columns.map((col) => ({
    ...col,
    label: col.title || col.label || col.key,
    ellipsis: true,
  }));
});

const dataTableRows = computed(() => {
  const rows = [];

  if (
    apexType.value === apexChartTypes.PIE ||
    apexType.value === apexChartTypes.DONUT ||
    apexType.value === apexChartTypes.RADIAL_BAR ||
    (props.chartType === "gauge_chart" && labels.value.length > 0)
  ) {
    const labelsArr = labels.value || [];
    const seriesArr = series.value || [];
    const formattedArr = formattedValues.value || [];

    labelsArr.forEach((label, index) => {
      rows.push({
        key: `row_${index}`,
        label: formattedLabels.value[index] ?? label,
        value:
          formattedArr[index] ||
          seriesArr[index]?.toLocaleString?.() ||
          seriesArr[index] ||
          "0",
      });
    });

    return rows;
  }

  const categoriesArr = categories.value || [];
  const seriesArr = series.value || [];

  if (seriesArr.length > 0 && seriesArr[0]?.data) {
    const fmtValues = formattedValues.value || [];
    seriesArr.forEach((s, seriesIdx) => {
      const seriesName = s.name || "Value";
      const dataArr = s.data || [];
      const fmtArr = fmtValues[seriesIdx] || [];

      categoriesArr.forEach((cat, index) => {
        const value = dataArr[index];
        if (value !== undefined && value !== null) {
          rows.push({
            key: `row_${seriesName}_${index}`,
            label: seriesArr.length > 1 ? `${cat} (${seriesName})` : cat,
            value:
              fmtArr[index] ||
              (typeof value === "number" ? value.toLocaleString() : value),
          });
        }
      });
    });
  } else if (categoriesArr.length > 0 && seriesArr.length > 0) {
    const formattedArr = formattedValues.value || [];
    categoriesArr.forEach((cat, index) => {
      const value = seriesArr[index];
      if (value !== undefined && value !== null) {
        rows.push({
          key: `row_${index}`,
          label: cat,
          value:
            formattedArr[index] ||
            (typeof value === "number" ? value.toLocaleString() : value),
        });
      }
    });
  }

  return rows;
});

const summarizedPage = ref(1);
const SUMMARIZED_PAGE_SIZE = 10;

const summarizedPaginatedRows = computed(() => {
  const allRows = dataTableRows.value;
  const start = (summarizedPage.value - 1) * SUMMARIZED_PAGE_SIZE;
  return allRows.slice(start, start + SUMMARIZED_PAGE_SIZE);
});

const summarizedSimplePagination = computed(() => {
  const total = dataTableRows.value.length;
  if (total === 0) return null;
  return {
    current: summarizedPage.value,
    pageSize: SUMMARIZED_PAGE_SIZE,
    total,
    showCount: true,
    showFirstLastPage: true,
    maxDataLimit: total,
  };
});

const handleSummarizedPaginationUpdate = (updatedPagination) => {
  summarizedPage.value = updatedPagination.current;
};

const extractColumnsFromConfig = () => {
  const config = chartConfiguration.value || {};
  const columns = [];

  chartConfigFieldKeys.forEach((key) => {
    if (config[key] && Array.isArray(config[key])) {
      config[key].forEach((item) => {
        if (item?.id && !columns.find((c) => c.id === item.id)) {
          columns.push({ id: item.id });
        }
      });
    }
  });

  return columns;
};

const buildUnsummarizedPayload = () => {
  const columns = extractColumnsFromConfig();

  return {
    type: dashboardType.value,
    chart_type: "table",
    primary_object_id: primaryObjectID.value,
    secondary_object_ids: secondaryObjectIDs.value || [],
    chart_configuration: JSON.stringify(decomposeConfigForBackend({ columns })),
    filters: JSON.stringify(decomposeFiltersForBackend(filters.value || { groups: [] })),
    calculated_fields: JSON.stringify(calculatedFields.value || []),
  };
};

const fetchUnsummarizedData = async (page = 1) => {
  if (!dashboardId.value) return;

  try {
    unsummarizedLoading.value = true;
    const payload = buildUnsummarizedPayload();
    const params = { page, page_size: unsummarizedPageSize };

    const response = await AnalyticService.V3.executeDashboardQuery(
      dashboardId.value,
      payload,
      params,
    );

    if (response?.success && response?.data?.chart_data) {
      const chartData = response.data.chart_data;
      unsummarizedData.value = {
        rows: (chartData.rows || []).map((row, idx) => ({ ...row, key: idx })),
        columns: chartData.columns || [],
        pagination: chartData.pagination || null,
      };
    }
  } catch (error) {
    console.error("An error occurred while fetching unsummarized data:", error);
  } finally {
    unsummarizedLoading.value = false;
  }
};

const unsummarizedHeaders = computed(() => {
  return (unsummarizedData.value.columns || []).map((col) => ({
    ...col,
    label: col.title || col.label || col.key,
    width: 180,
    ellipsis: true,
  }));
});

const unsummarizedSimplePagination = computed(() => {
  const pagination = unsummarizedData.value.pagination;
  const rowCount = unsummarizedData.value.rows.length;
  if (rowCount === 0) return null;
  return {
    current: pagination?.page || 1,
    pageSize: pagination?.page_size || unsummarizedPageSize,
    total: pagination?.total || rowCount,
    showCount: true,
    showFirstLastPage: true,
    maxDataLimit: pagination?.total || rowCount,
  };
});

const handleUnsummarizedPaginationUpdate = (updatedPagination) => {
  fetchUnsummarizedData(updatedPagination.current);
};

watch(activeDataTab, (newTab) => {
  if (newTab === 1 && !unsummarizedFetched.value) {
    unsummarizedFetched.value = true;
    fetchUnsummarizedData();
  }
});

watch(
  () => [props.chartData, props.chartType, decimalPlaces.value],
  () => {
    if (chartRef.value && chartRef.value.chart) {
      chartRef.value.chart.updateOptions(chartOptions.value);
      chartRef.value.chart.updateSeries(chartSeries.value);
    }
    summarizedPage.value = 1;
    unsummarizedFetched.value = false;
    unsummarizedData.value = { rows: [], columns: [], pagination: null };
  },
  { deep: true },
);
</script>

<style scoped>

.chart_section {
  height: calc(100% - 32px);
  width: calc(100% - 32px);
}

.table_section {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table_section :deep(.custom_tab) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.table_section :deep(.custom_tab > .overflow-y-auto) {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
  max-height: none !important;
  height: 100% !important;
}

.table_section :deep(.tab-pane) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.table_section :deep(.custom_tab > .overflow-y-auto > *) {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.data_table_container {
  @apply w-full rounded-xl;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.data_table_container :deep(.ant-spin-nested-loading) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.data_table_container :deep(.ant-spin-container) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.data_table_container :deep(.surely-table-wrapper) {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.data_table_container :deep(.surely-table-wrapper .surely-table) {
  flex: 1;
  min-height: 0;
}

.data_table_container :deep(.surely-table-header-cell) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.data_table_container :deep(.surely-table-body-cell) {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.data_table_container :deep(.pagination) {
  flex-shrink: 0;
  margin-top: 8px;
}
</style>
